import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API } from "@/lib/api";
import { rupeesExact, rupeesSmart } from "@/lib/format";

/**
 * `/bill/:token` — the public, read-only, tokenised bill link (P2-002, screen 6b;
 * INVOICE_PORT_SPEC §5.4).
 *
 * **Deliberately outside the city-scoped router.** Every other page in this app lives under
 * `/<city>/...` and needs the tenant already resolved before it can render anything — but this
 * link is handed to a customer who has no reason to know, or care, which city portal it belongs
 * to (the tenant is inside the signed token, verified server-side). So `App.js`'s `Shell`
 * renders this component straight off `window.location.pathname`, before city resolution, and
 * this component asks the backend directly with no `Authorization` header at all: nobody is
 * signed in on the other end of this link.
 *
 * **NOT a payment page — but it grew a "Pay" button.** The document itself still writes no form
 * and no script; the pay bar is chrome OUTSIDE the iframe, and it is a plain `<a href={upi_link}>`
 * deep link into whatever UPI app the customer already has. Apna Shahar never touches the money.
 *
 * **The `/meta` fetch is best-effort.** It only feeds the pay bar and the amount in it — a bill
 * must always render even if `/meta` 404s, rate-limits or the shape changes.
 */

const DEFAULT_FORMAT = "a4";
const RECEIPT_FORMAT = "thermal_80";

function tokenFromPath() {
  return decodeURIComponent((window.location.pathname.match(/^\/bill\/([^/]+)/) || [, ""])[1] || "");
}

export default function PublicBill() {
  const [state, setState] = useState({ status: "loading" });
  const [meta, setMeta] = useState(null);
  const [receiptView, setReceiptView] = useState(false);
  const iframeRef = useRef(null);
  const token = useMemo(() => tokenFromPath(), []);
  const format = receiptView ? RECEIPT_FORMAT : DEFAULT_FORMAT;

  const loadBill = useCallback(
    (fmt) => {
      if (!token) {
        setState({ status: "error" });
        return;
      }
      // Omit `?format=` for the default so the request stays the plain, cacheable bill URL —
      // only a non-default format (the receipt-view toggle) adds the query string.
      const qs = fmt && fmt !== DEFAULT_FORMAT ? `?format=${encodeURIComponent(fmt)}` : "";
      fetch(`${API}/public/bill/${encodeURIComponent(token)}${qs}`)
        .then(async (r) => {
          if (!r.ok) {
            setState({ status: "error" });
            return;
          }
          const html = await r.text();
          setState({ status: "ready", html });
        })
        .catch(() => setState({ status: "error" }));
    },
    [token],
  );

  useEffect(() => {
    loadBill(format);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format, loadBill]);

  // Best-effort: a meta failure must never break the bill itself.
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/public/bill/${encodeURIComponent(token)}/meta`)
      .then(async (r) => {
        if (!r.ok) return;
        const data = await r.json();
        setMeta(data);
      })
      .catch(() => {
        /* the pay bar just stays hidden */
      });
  }, [token]);

  const printBill = () => {
    try {
      iframeRef.current?.contentWindow?.print();
    } catch {
      /* nothing to do — the browser's own print dialog handles the rest when it works */
    }
  };

  if (state.status === "loading") {
    return (
      <div className="grid min-h-screen place-items-center text-muted-foreground" data-testid="public-bill-loading">
        …
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="grid min-h-screen place-items-center px-6 text-center" data-testid="public-bill-error">
        <p className="text-sm text-muted-foreground">This bill link is invalid or has been removed.</p>
      </div>
    );
  }

  const showPay = Number(meta?.balance_due_minor_units) > 0 && meta?.upi_link;
  // A part-paid bill reads as three plain figures instead of only "Pay ₹800" (G7). Shown only when
  // something has been paid AND something is still due; never guessed from a missing field.
  const showBreakdown =
    Number(meta?.paid_minor_units) > 0 &&
    Number(meta?.balance_due_minor_units) > 0 &&
    Number(meta?.total_minor_units) > 0;

  return (
    <div className="flex min-h-screen flex-col bg-muted/30" data-testid="public-bill-page">
      <div
        className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 border-b bg-background px-3 py-2 shadow-sm"
        data-testid="public-bill-bar"
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={printBill}
            className="rounded-full border px-3 py-1.5 text-xs font-semibold"
            data-testid="public-bill-print"
          >
            Print / Save as PDF
          </button>
          <button
            type="button"
            onClick={() => setReceiptView((v) => !v)}
            className="rounded-full border px-3 py-1.5 text-xs font-semibold"
            aria-pressed={receiptView}
            data-testid="public-bill-receipt-toggle"
          >
            Receipt view
          </button>
        </div>
        {showBreakdown && (
          <p className="text-xs font-semibold tabular-nums" data-testid="public-bill-breakdown">
            {`${rupeesSmart(meta.total_minor_units)} bill · ${rupeesSmart(meta.paid_minor_units)} paid · ${rupeesSmart(meta.balance_due_minor_units)} due`}
          </p>
        )}
        {showPay && (
          <a
            href={meta.upi_link}
            className="rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground"
            data-testid="public-bill-pay"
          >
            {`Pay ${rupeesExact(meta.balance_due_minor_units)} by UPI`}
          </a>
        )}
      </div>
      <iframe
        ref={iframeRef}
        title="Bill"
        srcDoc={state.html}
        data-testid="public-bill-frame"
        style={{ border: 0, width: "100%", flex: 1 }}
      />
    </div>
  );
}
